import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { MailboxProvider } from "@/contexts/MailboxContext";
import { Toaster } from "@/components/ui/sonner";

import AppHeader from "@/components/AppHeader";
import BetaBadge from "@/components/BetaBadge";
import ChatDrawer from "@/components/ChatDrawer";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import DigitalICard from "@/pages/DigitalICard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Procurement from "@/pages/Procurement";
import Attendance from "@/pages/Attendance";
import Auditions from "@/pages/Auditions";
import Vendor from "@/pages/Vendor";
import Pricing from "@/pages/Pricing";
import ICardGallery from "@/pages/ICardGallery";
import ICardMockups from "@/pages/ICardMockups";
import TeamJoin from "@/pages/TeamJoin";
import Settings from "@/pages/Settings";
import Discover from "@/pages/Discover";
import PublicProfile from "@/pages/PublicProfile";
import ProfileEdit from "@/pages/ProfileEdit";
import Inbox from "@/pages/Inbox";
import GrantOverride from "@/pages/GrantOverride";
import OverrideInvite from "@/pages/OverrideInvite";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Onboard from "@/pages/Onboard";
import OnboardProductionHouse from "@/pages/OnboardProductionHouse";
import OnboardTradeOrg from "@/pages/OnboardTradeOrg";
import OnboardStudio from "@/pages/OnboardStudio";
import OnboardStudioType from "@/pages/OnboardStudioType";
import OnboardVendor from "@/pages/OnboardVendor";
import OnboardInvestor from "@/pages/OnboardInvestor";
import OnboardGeneric from "@/pages/OnboardGeneric";
import OnboardAgencyType from "@/pages/OnboardAgencyType";
import OnboardCastingType from "@/pages/OnboardCastingType";
import OnboardL10Details from "@/pages/OnboardL10Details";
import OnboardDistributorType from "@/pages/OnboardDistributorType";
import OnboardL13Details from "@/pages/OnboardL13Details";
import DistributorSlate from "@/pages/DistributorSlate";
import SalesAgentDeals from "@/pages/SalesAgentDeals";
import SlateDiscovery from "@/pages/SlateDiscovery";
import ProducerSalesDeals from "@/pages/ProducerSalesDeals";
import ProducerScreeners from "@/pages/ProducerScreeners";
import DistributorInterests from "@/pages/DistributorInterests";
import TradeUnionProfile from "@/pages/TradeUnionProfile";
import TradeUnionDashboard from "@/pages/TradeUnionDashboard";
import ClaimTradeUnion from "@/pages/ClaimTradeUnion";
import OnboardIndividual from "@/pages/OnboardIndividual";
import TheaterGroupDashboard from "@/pages/TheaterGroupDashboard";
import TheaterHallDashboard from "@/pages/TheaterHallDashboard";
import TheaterHallDiscovery from "@/pages/TheaterHallDiscovery";
import TheaterGroupBookings from "@/pages/TheaterGroupBookings";
import TheaterHallBookings from "@/pages/TheaterHallBookings";
import PublicTheaterGroup from "@/pages/PublicTheaterGroup";
import PublicTheaterHall from "@/pages/PublicTheaterHall";
import PendingIndividuals from "@/pages/PendingIndividuals";
import AdminPendingOrgs from "@/pages/AdminPendingOrgs";
import AdminAffiliations from "@/pages/AdminAffiliations";
import Directory from "@/pages/Directory";
import DirectoryOrgs from "@/pages/DirectoryOrgs";
import MyDesk from "@/pages/MyDesk";
import CubicleInvitePreview from "@/pages/CubicleInvitePreview";
import OfficeVault from "@/pages/OfficeVault";
import Vouch from "@/pages/Vouch";
import OrgDashboardRouter from "@/pages/OrgDashboardRouter";
import AgencyAttendance from "@/pages/AgencyAttendance";
import DeskDetail from "@/pages/DeskDetail";
import SiteInventory from "@/pages/SiteInventory";
import ClientDetail from "@/pages/ClientDetail";
import Headcount from "@/pages/Headcount";
import AgencyPublicProfile from "@/pages/AgencyPublicProfile";
import UserPublicProfile from "@/pages/UserPublicProfile";
import ProjectDashboard from "@/pages/ProjectDashboard";
import ProjectCubicles from "@/pages/ProjectCubicles";
import ProjectRecce from "@/pages/ProjectRecce";
import ProjectShotList from "@/pages/ProjectShotList";
import ProjectRushes from "@/pages/ProjectRushes";
import ProjectLUTs from "@/pages/ProjectLUTs";
import ProjectAttendanceHeatmap from "@/pages/ProjectAttendanceHeatmap";
import ShowDesksList from "@/pages/channel/ShowDesksList";
import AttachShowDesk from "@/pages/channel/AttachShowDesk";
import ShowDeskDetail from "@/pages/channel/ShowDeskDetail";
import AcceptShowDeskInvite from "@/pages/channel/AcceptShowDeskInvite";
import ScriptAckLanding from "@/pages/tv/ScriptAckLanding";
import ProjectContinuity from "@/pages/ProjectContinuity";
import MyContinuity from "@/pages/MyContinuity";
import NudgeAckLanding from "@/pages/NudgeAckLanding";
import {
  OrgCallSheetBrandingPage, ProjectCallSheetBrandingPage,
} from "@/pages/CallSheetBranding";
import AssociationsDirectory from "@/pages/AssociationsDirectory";
import AssociationProfile from "@/pages/AssociationProfile";
import AssociationAnalytics from "@/pages/AssociationAnalytics";
import UnionClaim from "@/pages/UnionClaim";
import MyClaims from "@/pages/MyClaims";
import AdminUnionClaims from "@/pages/AdminUnionClaims";
import PayoutAccountSetup from "@/pages/PayoutAccountSetup";
import AdminPayoutAccounts from "@/pages/AdminPayoutAccounts";
import CommitteeVerify from "@/pages/CommitteeVerify";
import PrivacyGatekeeper from "@/pages/PrivacyGatekeeper";

function Shell() {
  const { user } = useAuth();
  const loc = useLocation();
  // Hide header on auth screens
  const hideHeader = ["/login", "/register"].includes(loc.pathname)
    || loc.pathname.startsWith("/invite/")
    || loc.pathname.startsWith("/team/join/")
    || loc.pathname.startsWith("/committee/verify/")
    || loc.pathname === "/onboard"
    || loc.pathname.startsWith("/onboard/");
  return (
    <>
      {!hideHeader && <AppHeader />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/pricing" element={<Pricing />} />

        <Route path="/dashboard" element={<ProtectedRoute><OrgDashboardRouter /></ProtectedRoute>} />
        <Route path="/icard" element={<ProtectedRoute><DigitalICard /></ProtectedRoute>} />
        <Route path="/icard/gallery" element={<ICardGallery />} />
        <Route path="/icard/mockups" element={<ProtectedRoute><ICardMockups /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
        <Route path="/projects/:id/cubicles" element={<ProtectedRoute><ProjectCubicles /></ProtectedRoute>} />
        <Route path="/procurement" element={<ProtectedRoute><Procurement /></ProtectedRoute>} />
        <Route path="/attendance" element={<ProtectedRoute><Attendance /></ProtectedRoute>} />
        <Route path="/office/attendance" element={<ProtectedRoute><AgencyAttendance /></ProtectedRoute>} />
        <Route path="/office/desks/site" element={<ProtectedRoute><SiteInventory /></ProtectedRoute>} />
        <Route path="/office/desks/client/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
        <Route path="/office/desks/:kind" element={<ProtectedRoute><DeskDetail /></ProtectedRoute>} />
        <Route path="/office/headcount" element={<ProtectedRoute><Headcount /></ProtectedRoute>} />
        <Route path="/office/public-profile" element={<ProtectedRoute><AgencyPublicProfile mode="owner" /></ProtectedRoute>} />
        <Route path="/agency/:handle" element={<AgencyPublicProfile mode="public" />} />
        <Route path="/me/public-profile" element={<ProtectedRoute><UserPublicProfile mode="owner" /></ProtectedRoute>} />
        <Route path="/u/:handle" element={<UserPublicProfile mode="public" />} />
        <Route path="/auditions" element={<ProtectedRoute><Auditions /></ProtectedRoute>} />
        <Route path="/vendor" element={<ProtectedRoute><Vendor /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/privacy" element={<ProtectedRoute><PrivacyGatekeeper /></ProtectedRoute>} />
        <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
        <Route path="/profile/me/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
        <Route path="/profile/:id" element={<ProtectedRoute><PublicProfile /></ProtectedRoute>} />
        <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
        <Route path="/grant-override" element={<ProtectedRoute><GrantOverride /></ProtectedRoute>} />
        <Route path="/invite/:token" element={<OverrideInvite />} />
        <Route path="/team/join/:token" element={<TeamJoin />} />
        <Route path="/committee/verify/:token" element={<CommitteeVerify />} />
        <Route path="/pricing/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/onboard" element={<Onboard />} />
        <Route path="/onboard/production-house" element={<OnboardProductionHouse />} />
        <Route path="/onboard/trade-org" element={<OnboardTradeOrg />} />
        <Route path="/onboard/studio"         element={<OnboardStudioType />} />
        <Route path="/onboard/studio-details" element={<OnboardStudio />} />
        <Route path="/onboard/vendor" element={<OnboardVendor />} />
        <Route path="/onboard/investor" element={<OnboardInvestor />} />
        <Route path="/onboard/advertising-agency" element={<OnboardGeneric kind="advertising_agency" />} />
        <Route path="/onboard/agency-type" element={<OnboardAgencyType />} />
        <Route path="/onboard/studio-network"     element={<OnboardGeneric kind="studio_network" />} />
        <Route path="/onboard/event-production"   element={<OnboardGeneric kind="event_production" />} />
        <Route path="/onboard/pr-agency"          element={<OnboardGeneric kind="pr_agency" />} />
        <Route path="/onboard/distributor"        element={<OnboardDistributorType />} />
        <Route path="/onboard/l13-details"        element={<OnboardL13Details />} />
        <Route path="/onboard/exhibitor"          element={<OnboardGeneric kind="exhibitor" />} />
        <Route path="/onboard/casting-agency"     element={<OnboardCastingType />} />
        <Route path="/onboard/l10-details"        element={<OnboardL10Details />} />
        <Route path="/trade-union/:slug"           element={<TradeUnionProfile />} />
        <Route path="/trade-union-dashboard/:slug" element={<TradeUnionDashboard />} />
        <Route path="/claim/:slug"                 element={<ClaimTradeUnion />} />
        <Route path="/onboard/journalist"         element={<OnboardIndividual kind="journalist" />} />
        <Route path="/onboard/talent-model"       element={<OnboardIndividual kind="talent_model" />} />
        <Route path="/onboard/theater-group"      element={<OnboardGeneric kind="theater_group" />} />
        <Route path="/onboard/theater-hall"       element={<OnboardGeneric kind="theater_hall" />} />
        <Route path="/pending-individuals"        element={<ProtectedRoute><PendingIndividuals /></ProtectedRoute>} />
        <Route path="/admin/pending-orgs"          element={<ProtectedRoute><AdminPendingOrgs /></ProtectedRoute>} />
        <Route path="/admin/affiliations"          element={<ProtectedRoute><AdminAffiliations /></ProtectedRoute>} />
        <Route path="/directory"                    element={<ProtectedRoute><Directory /></ProtectedRoute>} />
        <Route path="/directory/orgs"               element={<ProtectedRoute><DirectoryOrgs /></ProtectedRoute>} />
        <Route path="/associations"                 element={<AssociationsDirectory />} />
        <Route path="/associations/:id"             element={<AssociationProfile />} />
        <Route path="/associations/:id/analytics"   element={<ProtectedRoute><AssociationAnalytics /></ProtectedRoute>} />
        <Route path="/associations/:id/claim"       element={<ProtectedRoute><UnionClaim /></ProtectedRoute>} />
        <Route path="/my-claims"                    element={<ProtectedRoute><MyClaims /></ProtectedRoute>} />
        <Route path="/admin/union-claims"           element={<ProtectedRoute><AdminUnionClaims /></ProtectedRoute>} />
        <Route path="/payout-account"               element={<ProtectedRoute><PayoutAccountSetup /></ProtectedRoute>} />
        <Route path="/admin/payout-accounts"        element={<ProtectedRoute><AdminPayoutAccounts /></ProtectedRoute>} />
        <Route path="/me"                           element={<ProtectedRoute><MyDesk /></ProtectedRoute>} />
        <Route path="/cubicle-invites/:invite_id/preview" element={<ProtectedRoute><CubicleInvitePreview /></ProtectedRoute>} />
        <Route path="/office/vault"                     element={<ProtectedRoute><OfficeVault /></ProtectedRoute>} />
        <Route path="/vouch/:token"               element={<ProtectedRoute><Vouch /></ProtectedRoute>} />
        <Route path="/p/:id" element={<ProtectedRoute><ProjectDashboard /></ProtectedRoute>} />
        <Route path="/p/:id/recce"    element={<ProtectedRoute><ProjectRecce /></ProtectedRoute>} />
        <Route path="/p/:id/shots"    element={<ProtectedRoute><ProjectShotList /></ProtectedRoute>} />
        <Route path="/p/:id/rushes"   element={<ProtectedRoute><ProjectRushes /></ProtectedRoute>} />
        <Route path="/p/:id/luts"     element={<ProtectedRoute><ProjectLUTs /></ProtectedRoute>} />
        <Route path="/p/:id/heatmap"  element={<ProtectedRoute><ProjectAttendanceHeatmap /></ProtectedRoute>} />
        <Route path="/p/:id/continuity" element={<ProtectedRoute><ProjectContinuity /></ProtectedRoute>} />
        <Route path="/p/:id/branding" element={<ProtectedRoute><ProjectCallSheetBrandingPage /></ProtectedRoute>} />
        <Route path="/my/continuity" element={<ProtectedRoute><MyContinuity /></ProtectedRoute>} />
        <Route path="/settings/callsheet-branding" element={<ProtectedRoute><OrgCallSheetBrandingPage /></ProtectedRoute>} />
        <Route path="/distributor/slate"   element={<ProtectedRoute><DistributorSlate /></ProtectedRoute>} />
        <Route path="/sales-agent/deals"   element={<ProtectedRoute><SalesAgentDeals /></ProtectedRoute>} />
        <Route path="/slate/discovery"     element={<ProtectedRoute><SlateDiscovery /></ProtectedRoute>} />
        <Route path="/producer/sales-deals" element={<ProtectedRoute><ProducerSalesDeals /></ProtectedRoute>} />
        <Route path="/producer/screeners"  element={<ProtectedRoute><ProducerScreeners /></ProtectedRoute>} />
        <Route path="/distributor/interests" element={<ProtectedRoute><DistributorInterests /></ProtectedRoute>} />
        <Route path="/theater/halls"          element={<ProtectedRoute><TheaterHallDiscovery /></ProtectedRoute>} />
        <Route path="/theater-group/bookings" element={<ProtectedRoute><TheaterGroupBookings /></ProtectedRoute>} />
        <Route path="/theater-hall/bookings"  element={<ProtectedRoute><TheaterHallBookings /></ProtectedRoute>} />
        <Route path="/theater-group/:handle"  element={<PublicTheaterGroup />} />
        <Route path="/theater-hall/:handle"   element={<PublicTheaterHall />} />
        <Route path="/channel/show-desks"        element={<ProtectedRoute><ShowDesksList /></ProtectedRoute>} />
        <Route path="/channel/show-desks/attach" element={<ProtectedRoute><AttachShowDesk /></ProtectedRoute>} />
        <Route path="/channel/show-desks/invites/:token/accept" element={<AcceptShowDeskInvite />} />
        <Route path="/channel/show-desks/:id"    element={<ProtectedRoute><ShowDeskDetail /></ProtectedRoute>} />
        <Route path="/script-ack/:token" element={<ScriptAckLanding />} />
        <Route path="/nudge-ack/:token" element={<NudgeAckLanding />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <MailboxProvider>
            <Shell />
            <ChatDrawer />
            <BetaBadge />
            <InstallAppPrompt />
            <Toaster theme="dark" position="top-right" />
          </MailboxProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
